
<script setup>
import BaseModal from '@/components/Alerts/BaseModal.vue'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import InputText from '@/components/Forms/InputText.vue'
import { ref, watch } from 'vue'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Toaster } from '@/models/utils/Toaster.js';
import { documentsApi } from '@/api/Documents/DocumentsApi.js'
import { useAuthStore } from '@/stores/authStore.js'
import UserLookupComboBox from '@/components/Forms/UserLookupComboBox.vue'
import MultiListBox from '@/components/Forms/MultiListBox.vue'
import { ApiGatewayResponse, HttpStatusCode } from '@/api/ApiGateway.js'
import { UserpilotService } from '@/models/utils/UserpilotUtils.js'

const props = defineProps({
	show: { type: Boolean, default: false },//Show modal
	tagData: { type: Array, default: () => [] },
	modalError: { type: Object, default: () => ({ 'error_eh': false, 'error_msg': 'There was an error loading tags. Please try reloading the page.' }) },
	modalData: { type: Object, default: () => ({ "document_name": "", "comment": "", "tag_id": 0 }) },
})

const emit = defineEmits(['evCloseModal'])//Used by ManageDocumentsPage to close modal
const authStore = useAuthStore()
const toaster = new Toaster();

const
	document_name = ref(''),
	comment = ref(''),
	validationErrors = ref(null),
	showDiscardChangesModal = ref(false),
	showRejectModal = ref(false),
	selectedTags = ref([]),
	selectedUsers = ref([]),
	isSaving = ref(false);

watch(
	() => props.show,
	(newValue) =>
	{
		if (newValue)
		{
			//Load data when shown
			handleData(newValue)
		}
	}
)

function handleData()
{
	document_name.value = props.modalData.name
	//comment.value = props.modalData.latest_status_change.comment

	selectedTags.value = []

	for (let i = 0; i < props.modalData.tags.length; i++)
	{
		selectedTags.value.push(props.tagData.find(p_tag => p_tag.id === props.modalData.tags[i].id))
	}

	for (let i = 0; i < props.modalData.authorized_users.length; i++)
	{
		const rec = props.modalData.authorized_users[i]
		selectedUsers.value.push({ id: rec.user_id, family_name: rec.user_family_name, given_name: rec.user_given_name, email: rec.user_email})
	}
}


/**
 * Checks if there are any changes made to the modal
 * @returns {boolean}
 */
function changesMade()
{
	//let data = props.modalData

	return (comment.value != '')
}

/**
 * Show or Hide the Discard Changes modal
 * @param {boolean} p_show_eh True to show, false to hide
 */
function toggleDiscardChangesModal(p_show_eh)
{
	showDiscardChangesModal.value = p_show_eh
}

/**
 * Show or Hide the reject document modal
 * @param {boolean} p_show_eh True to show, false to hide
 */
function toggleRejectModal(p_show_eh)
{
	if (p_show_eh && comment.value.trim() === '')
	{
		validationErrors.value = new ApiGatewayResponse({
			'status': HttpStatusCode.ValidationError,
			'response': {
				'data': {
					'errors': [{
						'title': 'Missing value',
						'status': 422,
						'detail': `Please provide a brief summary to reject this document.`,
						'source': `status.comment`
					}]
				}
			}
		}, null)
	}
	else
	{
		showRejectModal.value = p_show_eh
	}
}

/**
 * Publish document (create Published status record)
 */
function publishDocument()
{
	isSaving.value = true;
	documentsApi.publishDocument(
		{
			'document': {
				'id': props.modalData.id,
				'organization_id': authStore.activeOrganizationId,
				'name': document_name.value
			},
			'tags':{
				'ids': selectedTags.value.map(tag => tag.id)
			},
			'status': {
				'comment': comment.value,
			},
			'authorized_users': {
				'user_ids': selectedUsers.value.map(user => user.id),
			}
		},
		() => {
			isSaving.value = false;

			UserpilotService.track('documents_publish', {
				"user_id": authStore.getAuth.user().id,
				"organization_id": authStore.activeOrganizationId
			});

			closeModal(true, 'publish')
		},
		(p_err) => {
			isSaving.value = false;
			validationErrors.value = p_err.showErrorBanner()
			//modalError.value = { 'error_eh': true, 'error_msg': 'Error loading Tags. Please try reloading the page.' }
		}

	)
}

/**
 * Publish document (create Rejected status record)
 */
function rejectDocument()
{
	isSaving.value = true;
	documentsApi.rejectDocument(
		{
			'document': {
				'id': props.modalData.id,
				'organization_id': authStore.activeOrganizationId,
				'name': document_name.value
			},
			'tags':{
				'ids': selectedTags.value.map(tag => tag.id)
			},
			'status': {
				'comment': comment.value,
			},
			'authorized_users': {
				'user_ids': selectedUsers.value.map(user => user.id),
			}
		},
		() => {
			isSaving.value = false;
			closeModal(true, 'reject')
		},
		(p_err) => {
			isSaving.value = false;
			validationErrors.value = p_err.showErrorBanner()
			//modalError.value = { 'error_eh': true, 'error_msg': 'Error loading Tags. Please try reloading the page.' }
		}

	)
}

/**
 * Check if there's any changes, if not, proceed with closing the modal
 * @param p_reload_eh True to reload the upload grid
 */
function dismissModal(p_reload_eh)
{
	if (!p_reload_eh && !props.modalError.error_eh && changesMade())
	{
		toggleDiscardChangesModal(true)
	}
	else
	{
		closeModal(p_reload_eh)
	}
}

/**
 * Do cleanup, show success toast and tell the UploadDocumentPage to close the modal
 * @param p_reload_eh
 */
function closeModal(p_reload_eh, p_action)
{
	cleanupModal()
	if (p_reload_eh)
	{
		if (p_action === 'reject')
		{
			toaster.success('Your document has been successfully deleted.')
		}
		else
		{
			toaster.success('Your document has been successfully published.')
		}
	}
	emit('evCloseModal', 'publish', p_reload_eh)//This needs to be 'publish'
}

/**
 * Tidy up
 */
function cleanupModal()
{
	toggleDiscardChangesModal(false)
	toggleRejectModal(false)

	document_name.value = ''
	comment.value = ''
	selectedTags.value = []
	selectedUsers.value = []
	validationErrors.value = null
}
</script>

<template>
	<div>
		<BaseModal :show="show" border-classes="border-t-6 border-tertiary">
			<div class="w-136 h-192">
				<div class="flex justify-between border-b border-neutral w-full pb-6">
					<p class="flex-grow text-xl font-bold text-heading">Publish Document</p>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<div v-if="props.modalError.error_eh">
					<div class="w-130 mt-6 text-error font-bold">{{ props.modalError.error_msg }}</div>
				</div>

				<form>
					<div class="flex flex-col mt-5">
						<InputText label="Document name" :disabled="true" type="text" placeholder="" input-height="h-10" autocomplete="off"
									  v-model="document_name" :errors="validationErrors" error-field="document.name" icon="edit" />

						<MultiListBox id="tags" v-model="selectedTags" title="Tags" placeholder="Select a tag"
										  label-class="font-medium text-label !mb-2" input-height="h-10" icon="edit" maxSearchResultsHeight="max-h-60"
										  :items="tagData"
										  :disabled="true"
										  :show-options-on-top="false" />

						<UserLookupComboBox id="authorized_users" v-model="selectedUsers" title="Authorized Users" placeholder="Enter name or email"
												  label-class="font-medium text-label !mb-2" input-height="h-10" icon="edit" maxSearchResultsHeight="max-h-60"
												  endpoint="users/documents/userSearch"
												  :endpoint_params="{ 'organization_id': authStore.activeOrganizationId }"
												  :enterable="true" :show-options-on-top="false" />

						<div class="text-sm text-grayscale-body -mt-3">Leave blank to share with the entire organization.</div>

						<InputText label="Notes" class="mt-3" :required="false" type="textarea" placeholder="" input-height="h-10" autocomplete="off"
									  v-model="comment" :errors="validationErrors" error-field="status.comment" />

					</div>
					<!-- Save/Cancel -->
					<div class="flex w-full flex-col-reverse sm:flex-row mt-4">

						<SubmitButton label="Reject" :type="ButtonType.Warning" :size="ButtonSize.Small"
							:disabled="isSaving"
							class="md:mr-auto mt-1 md:mt-0"
							:loading="isSaving"
							@click="toggleRejectModal(true)"
						/>
						<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
										  class="mt-3 sm:mt-0" :class="{'sm:mr-4': true}" />
						<SubmitButton label="Publish" :size="ButtonSize.Small" @click.prevent="publishDocument()"  />

					</div>
				</form>
			</div>
		</BaseModal>

		<Teleport to="html">
			<MultiButtonModal
				:show="showDiscardChangesModal"
				icon="info"
				message="Are you sure?"
				subMessage="Leaving now will discard any changes made."
				:buttons="[
						new ModalButtonData(
							'Cancel', () => { toggleDiscardChangesModal(false) }, ButtonType.Outline, ButtonSize.Small
						),
						new ModalButtonData(
							'Yes', () => { closeModal(false) }, ButtonType.Default, ButtonSize.Small
						)
					]"
			/>
		</Teleport>

		<Teleport to="html">
			<MultiButtonModal
				:show="showRejectModal"
				icon="alert"
				title="Reject Document"
				message="Are you sure?"
				subMessage="Rejecting this document restarts the approval process."
				:buttons="[
						new ModalButtonData(
							'Cancel', () => { toggleRejectModal(false) }, ButtonType.Outline, ButtonSize.Small
						),
						new ModalButtonData(
							'Yes', () => { rejectDocument() }, ButtonType.Default, ButtonSize.Small
						)
					]"
			/>
		</Teleport>

	</div>
</template>

<style scoped>
</style>
